/**
 * HostShell — the getjar.com parent-page orchestrator for the host-rendered
 * ad bridge (ADR-001).
 *
 * Wires together:
 *   - the `HostController` postMessage listener
 *   - the `PacingGate` (moved here from the game-side SDK; the host sees the
 *     whole user session across game switches and is the only side that can
 *     enforce a cap that survives a game reload)
 *   - the two interstitial renderers (display via GPT, video via IMA), kept
 *     behind narrow interfaces so tests can pass fakes
 *
 * Attribution: the host trusts ONLY its own per-origin game registry — never
 * the iframe's self-claim in the handshake. The host shell registers
 * `{ appId, publisher }` for each game iframe it embeds; that's what flows
 * into the AY/GAM request via the renderer. The handshake payload is
 * advisory (used as a fallback when the host hasn't pre-registered) and is
 * superseded by any explicit `registerGame()` call.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { PacingGate } from '../core/pacing';
import { BidFreshnessGate } from '../core/bid-freshness';
import { mergeVastCustParams } from '../utils/targeting-builder';
import { HostController } from './host-controller';
export class HostShell {
    constructor(config) {
        var _a, _b, _c, _d;
        this.pacing = new PacingGate();
        /**
         * Bid freshness gates — one per bid-ahead-capable format.
         *
         * Today both gates are empty on every show because there's no
         * pre-fetch path yet (the show-time bid is fetched-and-consumed in
         * one go via `fetchBidTargeting`). When a follow-up wires a pre-fetch
         * at `query_ready` (rewarded) or at game-side hint for video, those
         * call sites will `prefetchBidTargeting()` which stores in the gate;
         * subsequent show-time `fetchBidTargeting()` calls then hit the
         * "fresh held bid, no re-fetch" path.
         *
         * Banner / display interstitial deliberately not included — they
         * render within seconds of the bid; no freshness gap to bridge.
         */
        this.bidFreshness = {
            video_interstitial: new BidFreshnessGate(),
            rewarded_video: new BidFreshnessGate(),
        };
        this.games = new Map();
        // Rewarded preload state. preload() is fire-and-forget on the first
        // query_ready and the readiness is reported back synchronously each call.
        // No retry / backoff yet — a failed preload simply reports ready:false
        // until the next attempt (subsequent query_ready re-triggers loading).
        this.rewardedPreload = { started: false, loaded: false };
        this.interstitialRenderer = config.interstitialRenderer;
        this.videoInterstitialRenderer = config.videoInterstitialRenderer;
        this.rewardedVideoRenderer = config.rewardedVideoRenderer;
        this.videoInterstitialTag = config.videoInterstitialTag;
        this.rewardedVideoTag = config.rewardedVideoTag;
        this.rewardValidator = config.rewardValidator;
        this.telemetry = config.telemetry;
        this.bidProvider = config.bidProvider;
        this.adUnits = (_a = config.adUnits) !== null && _a !== void 0 ? _a : {};
        this.viewport =
            (_b = config.viewport) !== null && _b !== void 0 ? _b : (() => ({
                w: typeof window !== 'undefined' ? window.innerWidth || 0 : 0,
                h: typeof window !== 'undefined' ? window.innerHeight || 0 : 0,
            }));
        this.now = (_c = config.now) !== null && _c !== void 0 ? _c : (() => Date.now());
        this.ayDebug = config.ayDebug === true;
        // Configure the pacing gate up front. Subsequent re-configures are
        // possible (e.g. on /ads/init refresh) via reconfigurePacing().
        this.pacing.configure((_d = config.pacingConfig) !== null && _d !== void 0 ? _d : null, this.now());
        if (config.games) {
            for (const [origin, ctx] of Object.entries(config.games)) {
                this.games.set(origin, ctx);
            }
        }
        this.controller = new HostController({
            allowedOrigins: config.allowedOrigins,
            ownWindow: config.ownWindow,
            // Host-known attribution: prefer the pre-registered context. The
            // handshake payload is the fallback — useful for dev/local where the
            // host page didn't manually register the iframe.
            onHandshake: (payload, origin) => {
                if (!this.games.has(origin)) {
                    this.games.set(origin, { appId: payload.appId, publisher: payload.publisher });
                }
            },
            onRequestShow: (payload, ctx) => this.handleRequestShow(payload, ctx.origin),
            onQueryReady: (payload) => this.handleQueryReady(payload),
        });
    }
    /** Attach the postMessage listener. Idempotent. */
    start() {
        this.controller.start();
    }
    /** Detach the listener. Idempotent. */
    stop() {
        this.controller.stop();
    }
    /** Explicit registration — overrides anything learned from a handshake. */
    registerGame(origin, ctx) {
        this.games.set(origin, ctx);
    }
    /** Apply a new pacing config (e.g. after a fresh /ads/init). */
    reconfigurePacing(config) {
        this.pacing.configure(config, this.now());
    }
    // --- request_show pipeline ----------------------------------------------
    /**
     * Entry point invoked by HostController on every `request_show`. Wrapped
     * in a NON-async function so the synchronous prologue runs before
     * V8 wraps the body in a Promise — that's what keeps
     * `prepareForActivation()` (and therefore `AdDisplayContainer.initialize()`)
     * inside the user-activation window propagated from the game's tap.
     *
     * Anything that touches `await` lives in `handleRequestShowAsync`.
     */
    handleRequestShow(payload, origin) {
        // SYNCHRONOUS PROLOGUE — no awaits permitted in this block.
        //
        // For rewarded_video we must capture the propagated user activation
        // (Chromium grants a ~5s transient window after the iframe tap, and
        // any subsequent IMA `play()` requires it). Calling
        // `AdDisplayContainer.initialize()` synchronously here is what makes
        // sound-on autoplay work without a host tap-gate. See ADR-001 risk #1.
        if (payload.format === 'rewarded_video' && this.rewardedVideoRenderer) {
            try {
                this.rewardedVideoRenderer.prepareForActivation();
            }
            catch (err) {
                // prepareForActivation must never throw — but if a future renderer
                // does, we log and continue; the worst case is autoplay_blocked
                // surfaces and our metric catches it.
                console.warn('[GetJarAds host] prepareForActivation threw:', err);
            }
        }
        // From here on, awaits are fine: the activation has been captured (or
        // wasn't relevant for this format).
        return this.handleRequestShowAsync(payload, origin);
    }
    handleRequestShowAsync(payload, origin) {
        return __awaiter(this, void 0, void 0, function* () {
            // 1. Look up host-known attribution. Refuse if we have none — we will
            //    not render an ad whose game we cannot attribute.
            const game = this.games.get(origin);
            if (!game) {
                // Treat as an error rather than no_ad so it surfaces in monitoring:
                // a request with no registered game is a host-configuration bug, not
                // a fill miss.
                return { outcome: 'error', reason: 'unknown_game_origin' };
            }
            // 2. Pacing gate. The host's PacingGate is the canonical one — any
            //    suppression here is final. Pacing applies to display + video
            //    interstitials uniformly. Rewarded is exempt (user-initiated and
            //    gated by the in-game offer button itself) — see ADR-001.
            if (payload.format !== 'rewarded_video') {
                const suppression = this.pacing.evaluate(this.now());
                if (suppression !== null) {
                    return { outcome: 'suppressed', reason: suppression };
                }
            }
            // 3. Render. Only the two interstitial formats route here today; the
            //    rewarded_video path will land in a follow-up.
            try {
                const outcome = yield this.render(payload, game);
                // recordShow only on a real impression so a no_ad/error/suppressed
                // doesn't count against the rolling cap.
                if (outcome.outcome === 'shown') {
                    this.pacing.recordShow(this.now());
                }
                else if (outcome.outcome === 'no_ad') {
                    this.logEmptyFill(payload, origin, game, outcome.reason);
                }
                return outcome;
            }
            catch (err) {
                // Classify the renderer rejection. GPTInterstitialAds rejects with
                // `new Error('No ad available')` on a truly empty render; everything
                // else is a real error and we surface it.
                const reason = err instanceof Error ? err.message : String(err);
                if (reason === 'No ad available') {
                    this.logEmptyFill(payload, origin, game, reason);
                    return { outcome: 'no_ad', reason };
                }
                return { outcome: 'error', reason };
            }
        });
    }
    render(payload, game) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const config = {
                placement: (_a = payload.placementName) !== null && _a !== void 0 ? _a : 'host_routed',
                placementName: payload.placementName,
                category: payload.category,
                gameId: game.appId,
            };
            if (payload.format === 'interstitial') {
                // Targeting carries the HOST-KNOWN attribution. We deliberately do
                // not trust payload-side claims for game_id / publisher.
                const baseTargeting = {
                    game_id: game.appId,
                    publisher: game.publisher,
                    source: 'sdk',
                    format: 'interstitial',
                    sdk_ad_type: 'interstitial',
                    is_rewarded: 'false',
                };
                if (payload.placementName)
                    baseTargeting.placement_name = payload.placementName;
                if (payload.category)
                    baseTargeting.category = payload.category;
                // Fetch hb_* from AY BEFORE the GPT slot is requested. Without this
                // the slot was requested with only host-known keys (game_id /
                // publisher / format / ...) and AY's prebid line items couldn't
                // match — GAM filled with its own demand instead (web-interstitial /
                // AdSense). prev_scp now carries hb_pb / hb_pb_<bidder> / hb_uuid /
                // hb_size etc. alongside the host-known attribution.
                //
                // Fail-open: a no-bid / provider error returns `{}` and the slot
                // still renders against GAM's house / AdSense demand. Pacing was
                // already checked before render() was reached; we don't re-check.
                const hbTargeting = yield this.fetchBidTargeting('interstitial', payload, game);
                const targeting = Object.assign(Object.assign({}, baseTargeting), hbTargeting);
                if (this.ayDebug) {
                    console.log('[GetJarAds host AY DEBUG] display interstitial → GPT slot targeting:', targeting);
                }
                yield this.interstitialRenderer.show(config, targeting);
                return { outcome: 'shown' };
            }
            if (payload.format === 'video_interstitial') {
                if (!this.videoInterstitialTag) {
                    return { outcome: 'error', reason: 'no_video_tag' };
                }
                // Run any AY bid AHEAD of the renderer so hb_* (especially hb_uuid /
                // hb_cache_url) is on cust_params when IMA fires the VAST request.
                // Without these keys the price-priority line items can't resolve a
                // cached creative — see AY-NEXT-STEPS § A.
                const hbExtras = yield this.fetchBidTargeting('video_interstitial', payload, game);
                const videoTag = mergeVastCustParams(this.videoInterstitialTag, hbExtras);
                if (this.ayDebug) {
                    console.log(`[GetJarAds host AY DEBUG] video interstitial → VAST URL (hb_* keys: ${Object.keys(hbExtras).join(',') || '(none)'}): ${videoTag}`);
                }
                yield this.videoInterstitialRenderer.show(config, videoTag);
                return { outcome: 'shown' };
            }
            if (payload.format === 'rewarded_video') {
                return this.renderRewarded(payload, game);
            }
            // Defensive default — every format the bridge accepts is wired above.
            return { outcome: 'error', reason: 'unknown_format' };
        });
    }
    /**
     * Rewarded-video render pipeline. The synchronous activation prep already
     * happened in handleRequestShow(); from here we kick off IMA and translate
     * its result into an `ad_result` that the game bridge resolves
     * `showRewardedVideo()` with.
     *
     *   - completed         → server-validate; granted reflects server truth
     *   - skipped            → granted:false, reason='skipped'
     *   - closed             → granted:false, reason='closed'
     *   - no_ad              → granted:false + empty_fill log
     *   - autoplay_blocked   → telemetry event 'ad_autoplay_blocked' + granted:false
     *   - error              → granted:false, reason from the renderer
     */
    renderRewarded(payload, game) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e;
            if (!this.rewardedVideoRenderer) {
                return { outcome: 'error', reason: 'format_not_routed_yet', granted: false };
            }
            if (!this.rewardedVideoTag) {
                return { outcome: 'error', reason: 'no_rewarded_tag', granted: false };
            }
            if (!payload.rewardType) {
                // The game contract guarantees this is set for rewarded_video.
                return { outcome: 'error', reason: 'missing_reward_type', granted: false };
            }
            const config = {
                placement: (_a = payload.placementName) !== null && _a !== void 0 ? _a : 'host_routed',
                placementName: payload.placementName,
                category: payload.category,
                gameId: game.appId,
                rewardType: payload.rewardType,
                // The host doesn't bridge onRewarded back into the bundle; the bridge
                // ad_result resolves the game-side promise. Provide a noop so the
                // RewardedConfig type is satisfied without making it optional.
                onRewarded: () => { },
            };
            // AY bid + cust_params merge — same pre-show pattern as the video
            // interstitial path. Runs BEFORE the IMA show, so hb_uuid / hb_cache_url
            // are on the VAST URL by the time IMA fires the ad request. Note: this
            // does NOT consume the user-activation window — prepareForActivation()
            // (synchronous, called by handleRequestShow's prologue) is what
            // captured it. The bid fetch happens here on a Promise.
            const hbExtras = yield this.fetchBidTargeting('rewarded_video', payload, game);
            const rewardedTag = mergeVastCustParams(this.rewardedVideoTag, hbExtras);
            if (this.ayDebug) {
                console.log(`[GetJarAds host AY DEBUG] rewarded video → VAST URL (hb_* keys: ${Object.keys(hbExtras).join(',') || '(none)'}): ${rewardedTag}`);
            }
            let renderResult;
            try {
                renderResult = yield this.rewardedVideoRenderer.show(config, rewardedTag);
            }
            catch (err) {
                // Defensive — show() should resolve with an outcome rather than throw.
                const reason = err instanceof Error ? err.message : String(err);
                return { outcome: 'error', reason, granted: false, rewardType: payload.rewardType };
            }
            switch (renderResult.outcome) {
                case 'completed':
                    return this.validateAndPackage(payload, game, renderResult);
                case 'skipped':
                    return {
                        outcome: 'skipped',
                        reason: (_b = renderResult.reason) !== null && _b !== void 0 ? _b : 'skipped',
                        granted: false,
                        rewardType: payload.rewardType,
                    };
                case 'closed':
                    return {
                        outcome: 'closed',
                        reason: (_c = renderResult.reason) !== null && _c !== void 0 ? _c : 'closed',
                        granted: false,
                        rewardType: payload.rewardType,
                    };
                case 'no_ad':
                    this.logEmptyFill(payload, /* origin */ '(rewarded)', game, renderResult.reason);
                    return {
                        outcome: 'no_ad',
                        reason: (_d = renderResult.reason) !== null && _d !== void 0 ? _d : 'no_ad',
                        granted: false,
                        rewardType: payload.rewardType,
                    };
                case 'autoplay_blocked':
                    // Field canary — see ADR-001 risk #1. If this metric fires the
                    // revert is a one-line branch: render a host "Tap to watch" gate
                    // before show(), wait for the host-side tap, then call show().
                    this.emitTelemetry('ad_autoplay_blocked', {
                        format: 'rewarded_video',
                        placement: payload.placementName,
                        gameId: game.appId,
                        publisher: game.publisher,
                        reason: renderResult.reason,
                    });
                    return {
                        outcome: 'error',
                        reason: 'autoplay_blocked',
                        granted: false,
                        rewardType: payload.rewardType,
                    };
                case 'error':
                default:
                    return {
                        outcome: 'error',
                        reason: (_e = renderResult.reason) !== null && _e !== void 0 ? _e : 'render_error',
                        granted: false,
                        rewardType: payload.rewardType,
                    };
            }
        });
    }
    /**
     * Server-authoritative reward validation. Called only on IMA `completed`.
     * A missing validator denies the reward — the game's source of truth must
     * always be the host, never the renderer's view of the IMA stream.
     */
    validateAndPackage(payload, game, _result) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!this.rewardValidator) {
                return {
                    outcome: 'error',
                    reason: 'no_reward_validator',
                    granted: false,
                    rewardType: payload.rewardType,
                };
            }
            const context = JSON.stringify({
                placement: payload.placementName,
                adType: 'rewarded_video',
                gameId: game.appId,
                publisher: game.publisher,
            });
            let validation;
            try {
                validation = yield this.rewardValidator.validateReward(payload.rewardType, context);
            }
            catch (err) {
                // A validator-side error must NOT grant a reward.
                const reason = err instanceof Error ? err.message : String(err);
                return {
                    outcome: 'shown',
                    reason: `validator_error:${reason}`,
                    granted: false,
                    rewardType: payload.rewardType,
                };
            }
            if (!validation.granted) {
                return {
                    outcome: 'shown',
                    reason: (_a = validation.reason) !== null && _a !== void 0 ? _a : 'rejected_by_server',
                    granted: false,
                    rewardType: payload.rewardType,
                };
            }
            return {
                outcome: 'shown',
                granted: true,
                rewardType: payload.rewardType,
                amount: validation.amount,
            };
        });
    }
    /**
     * Readiness handler for `query_ready`. For rewarded_video this is what
     * keeps the game from showing a dead reward-offer button under AY-only
     * no-fill — the game should only display the offer once we report ready.
     * We trigger preload (IMA + bid fetch) on the first call so subsequent
     * polls can see it ready before the user's tap.
     *
     * Returns the renderer's current `isPreloaded()` view; preload itself
     * runs fire-and-forget so the reply isn't blocked on the SDK load.
     */
    handleQueryReady(payload) {
        return __awaiter(this, void 0, void 0, function* () {
            if (payload.format !== 'rewarded_video') {
                return { format: payload.format, ready: false };
            }
            if (!this.rewardedVideoRenderer) {
                return { format: 'rewarded_video', ready: false };
            }
            if (!this.rewardedPreload.started) {
                this.rewardedPreload.started = true;
                // Fire-and-forget. We deliberately do NOT await — query_ready needs
                // to reply quickly, and the game can poll again to learn when the
                // preload completes.
                this.rewardedVideoRenderer
                    .preload()
                    .then(() => {
                    this.rewardedPreload.loaded = true;
                })
                    .catch((err) => {
                    console.warn('[GetJarAds host] rewarded preload failed:', err);
                    // Reset `started` so the next query_ready re-triggers preload.
                    this.rewardedPreload.started = false;
                });
            }
            return {
                format: 'rewarded_video',
                ready: this.rewardedVideoRenderer.isPreloaded() || this.rewardedPreload.loaded,
            };
        });
    }
    /**
     * Pre-fetch a bid for `video_interstitial` or `rewarded_video` and stash
     * it in the freshness gate. Returns `true` on a stored bid, `false` on
     * no-fill / no provider — so callers (a future bid-ahead trigger at
     * `query_ready`, or a game-side hint) can decide whether to retry.
     *
     * Not yet called by any internal path: today's flow fetches at show
     * time. This method exists so the freshness machinery is reachable
     * (and test-covered) ahead of the architectural move.
     */
    prefetchBidTargeting(format, args) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.bidProvider)
                return false;
            try {
                const result = yield this.bidProvider.requestBidTargeting({
                    format,
                    placementName: args.placementName,
                    adUnit: this.adUnitForFormat(format),
                    viewport: this.viewport(),
                    gameId: args.gameId,
                    publisherId: args.publisherId,
                    category: args.category,
                });
                if (Object.keys(result.targeting).length === 0) {
                    this.bidFreshness[format].clear();
                    return false;
                }
                this.bidFreshness[format].store(result.targeting, result.expiresAt, this.now());
                return true;
            }
            catch (err) {
                console.warn('[GetJarAds host] prefetchBidTargeting rejected:', err);
                this.bidFreshness[format].clear();
                return false;
            }
        });
    }
    /**
     * Pull `hb_*` prebid targeting from the configured BidProvider for any
     * host-rendered format that needs a bid. Never throws — when the
     * provider isn't configured, returns an empty record (the renderer
     * plays against the bare slot / VAST tag). A throw from the provider
     * is logged and treated as no-bid; the show MUST NOT fail because of
     * a bidder hiccup.
     *
     * Freshness check (only for `video_interstitial` / `rewarded_video`):
     * if a previously-stored bid is still fresh, consume it and skip the
     * `/ads/bid` round-trip. If it's stale, emit `bid_stale_refetch`
     * telemetry and re-fetch — never serve a stale bid.
     */
    fetchBidTargeting(format, payload, game) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            if (!this.bidProvider) {
                if (this.ayDebug) {
                    console.log(`[GetJarAds host AY DEBUG] no bidProvider configured — skipping bid for format=${format}`);
                }
                return {};
            }
            // Freshness short-circuit — gate is only active for the two bid-ahead
            // formats. Display interstitial bypasses (no held-bid scenario by
            // design — renders within seconds of the bid).
            if (format === 'video_interstitial' || format === 'rewarded_video') {
                const gate = this.bidFreshness[format];
                const disposition = gate.evaluate(this.now());
                if (disposition === null) {
                    // Held bid is fresh. Consume and return without a /ads/bid call.
                    const targeting = (_a = gate.consume()) !== null && _a !== void 0 ? _a : {};
                    if (this.ayDebug) {
                        console.log(`[GetJarAds host AY DEBUG] held bid is fresh — consumed without re-fetch for format=${format}`, targeting);
                    }
                    return targeting;
                }
                if (disposition === 'expired' || disposition === 'expired_unknown') {
                    const peek = gate.peek();
                    const ageMs = peek ? this.now() - peek.fetchedAt : undefined;
                    gate.clear();
                    this.emitTelemetry('bid_stale_refetch', {
                        format,
                        placement: payload.placementName,
                        gameId: game.appId,
                        publisher: game.publisher,
                        disposition,
                        age_ms: ageMs,
                    });
                    if (this.ayDebug) {
                        console.log(`[GetJarAds host AY DEBUG] held bid stale (${disposition}, age_ms=${ageMs}) — re-fetching for format=${format}`);
                    }
                    // Fall through to the fetch path below; `disposition` is now
                    // recorded in telemetry and the gate is empty.
                }
                // disposition === 'no_bid' falls through to the fetch path with
                // no telemetry — that's the steady-state "first show, no
                // pre-fetched bid" case, not a regression.
            }
            const startedAt = Date.now();
            const input = {
                format,
                placementName: payload.placementName,
                adUnit: this.adUnitForFormat(format),
                viewport: this.viewport(),
                gameId: game.appId,
                publisherId: game.publisher,
                category: payload.category,
            };
            if (this.ayDebug) {
                console.log('[GetJarAds host AY DEBUG] /ads/bid OUT', input);
            }
            try {
                const result = yield this.bidProvider.requestBidTargeting(input);
                const targeting = (_b = result.targeting) !== null && _b !== void 0 ? _b : {};
                if (this.ayDebug) {
                    const keys = Object.keys(targeting);
                    console.log(`[GetJarAds host AY DEBUG] /ads/bid IN — ${Date.now() - startedAt}ms — ` +
                        `keys=${keys.length === 0 ? '(empty / no_bid)' : keys.join(',')} ` +
                        `expiresAt=${(_c = result.expiresAt) !== null && _c !== void 0 ? _c : '(none)'}`, targeting);
                }
                // No-fill on a re-fetch (i.e. we already cleared a stale held bid
                // above and the fresh fetch came back empty) is worth surfacing
                // separately — distinguishes "stale bid → no fill" from a regular
                // first-time no-fill.
                if ((format === 'video_interstitial' || format === 'rewarded_video') &&
                    Object.keys(targeting).length === 0) {
                    // We can't tell from here whether the gate had been "stale" vs
                    // "no_bid" before — the telemetry above already disambiguated.
                    // Still emit a refetch_no_fill counter so dashboards can JOIN
                    // the two events when needed.
                    this.emitTelemetry('bid_refetch_no_fill', {
                        format,
                        placement: payload.placementName,
                        gameId: game.appId,
                        publisher: game.publisher,
                    });
                }
                return targeting;
            }
            catch (err) {
                // Fail-open: a bidder error never blocks the show.
                console.warn('[GetJarAds host] bidProvider rejected, proceeding without hb_* targeting:', err);
                return {};
            }
        });
    }
    /** Map host-rendered formats to the configured GAM ad-unit path. */
    adUnitForFormat(format) {
        if (format === 'interstitial')
            return this.adUnits.interstitial;
        if (format === 'video_interstitial')
            return this.adUnits.videoInterstitial;
        return this.adUnits.rewardedVideo;
    }
    emitTelemetry(event, data) {
        if (!this.telemetry)
            return;
        try {
            const out = this.telemetry.emit(event, data);
            if (out && typeof out.catch === 'function') {
                out.catch((err) => console.warn(`[GetJarAds host] telemetry '${event}' rejected:`, err));
            }
        }
        catch (err) {
            console.warn(`[GetJarAds host] telemetry '${event}' threw:`, err);
        }
    }
    logEmptyFill(payload, origin, game, reason) {
        console.log('[GetJarAds host] empty_fill', {
            format: payload.format,
            placement: payload.placementName,
            origin,
            gameId: game.appId,
            publisher: game.publisher,
            reason: reason !== null && reason !== void 0 ? reason : 'no_ad',
        });
    }
}
